// Client-side validation for the operator's review link.
//
// This deliberately MIRRORS communications/lib/reviewLinks.js. The server is
// the authority - it re-validates at send time, because users/{uid} is writable
// by its owner - but the profile form needs to say "that link won't work"
// before someone saves it and wonders why no customer ever gets asked.
//
// reviewLink.test.js asserts the two allowlists stay identical, so this cannot
// drift into accepting something the server will silently refuse to send.

export const ALLOWED_SUFFIXES = ['google.com', 'g.page', 'goo.gl', 'facebook.com'];

/** True when the host is one we are willing to send customers to. */
export function isAllowedHost(hostname) {
  const host = String(hostname || '').toLowerCase();
  return ALLOWED_SUFFIXES.some((s) => host === s || host.endsWith(`.${s}`));
}

/**
 * Validate a review link.
 * @returns {string|null} the normalised URL, or null when the field is empty.
 * @throws {Error} with a message written for the operator, not for a log.
 */
export function normaliseReviewUrl(raw) {
  const trimmed = String(raw == null ? '' : raw).trim();
  if (!trimmed) return null;

  let parsed;
  try {
    parsed = new URL(trimmed);
  } catch (e) {
    throw new Error('That does not look like a web address. Paste the whole link, starting with https://');
  }

  if (parsed.protocol !== 'https:') {
    throw new Error('The link has to start with https://');
  }

  if (!isAllowedHost(parsed.hostname)) {
    throw new Error(
      `Links to ${parsed.hostname} are not allowed. Copy the review link from your Google Business Profile ` +
        'under "Ask for reviews" — it looks like https://g.page/r/…/review'
    );
  }

  return parsed.href;
}

/** The error message for a link, or null when it is fine (or empty). */
export function reviewUrlError(raw) {
  try {
    normaliseReviewUrl(raw);
    return null;
  } catch (error) {
    return error.message;
  }
}

/** Is this link usable as-is. An empty field is not an error, just unset. */
export function isValidReviewUrl(raw) {
  try {
    return normaliseReviewUrl(raw) !== null;
  } catch (error) {
    return false;
  }
}
